import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getSeccionesAcademicas } from '../../api/secciones_academicas';
import { getInstitutions } from '../../api/institutions';
import { getCampus } from '../../api/campus';
import { getAllAcademias } from '../../api/subjects';
import { getPrograms } from '../../api/programs';
import { getPlanesEstudioPorMateria, upsertCreditosPrograma } from '../../api/planesEstudio';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';

const defaultForm = {
  nombre: '',
  clave: '',
  tipo: '',
  institucion: '',
  seccion: '',
  academia: '',
  creditos: '',
  horasDocentesSemana: '',
  horasTotalesSemana: '',
  descripcion: '',
  objetivos: '',
  estado: 'ACTIVO',
};

export default function NewMateriaModal({ open, onClose, onSubmit, mode = 'create', readOnly = false, initialData = null }) {
  const [form, setForm] = useState(defaultForm);
  const dialogRef = useRef(null);
  const [secciones, setSecciones] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [academias, setAcademias] = useState([]);

  // --- Tabs ---
  const [activeTab, setActiveTab] = useState('general');

  // --- Tab Créditos por Programa ---
  const [planes, setPlanes] = useState([]);
  const [loadingPlanes, setLoadingPlanes] = useState(false);
  const [programas, setProgramas] = useState([]);
  const [loadingProgramas, setLoadingProgramas] = useState(false);
  const [creditoForm, setCreditoForm] = useState({ id_programa: '', creditos_asignados: '' });
  const [savingCredito, setSavingCredito] = useState(false);
  const [creditoMsg, setCreditoMsg] = useState(null); // {type:'ok'|'error', text}

  const idMateria = initialData?.id_materia ?? initialData?.id ?? null;
  const canUseCreditsTab = mode !== 'create' && idMateria;

  // Cargar datos iniciales cuando se abre el modal en modo view/edit
  useEffect(() => {
    if (open && initialData) {
      setForm({
        nombre: initialData.nombre || '',
        clave: initialData.clave || '',
        tipo: initialData.tipo || '',
        institucion: String(initialData.institucion || ''),
        seccion: String(initialData.seccion || ''),
        academia: String(initialData.academia || ''),
        creditos: initialData.creditos ?? '',
        horasDocentesSemana: initialData.horasDocentesSemana ?? '',
        horasTotalesSemana: initialData.horasTotalesSemana ?? '',
        descripcion: initialData.descripcion || '',
        objetivos: initialData.objetivos || '',
        estado: initialData.estado || 'ACTIVO',
      });
    } else if (open && !initialData) {
      setForm(defaultForm);
    }
  }, [open, initialData]);
  
  // Cargar catálogos al abrir el modal
  useEffect(() => {
    if (!open) return;
    
    // Cargar instituciones
    getInstitutions().then(data => {
      setInstituciones(data);
    }).catch(err => {
      console.error('[NewMateriaModal] Error al cargar instituciones:', err);
      setInstituciones([]);
    });
    
    // Cargar secciones académicas
    getSeccionesAcademicas().then(data => {
      setSecciones(data);
    }).catch(err => {
      console.error('[NewMateriaModal] Error al cargar secciones:', err);
      setSecciones([]);
    });

    // Cargar academias y mapear campos
    getAllAcademias().then(data => {
      console.log('[NewMateriaModal] Academias cargadas del backend:', data);
      // Mapear id_academia -> id, nombre_academia -> nombre
      const mapped = Array.isArray(data) ? data.map(a => ({
        id: String(a.id_academia || a.id),
        nombre: a.nombre_academia || a.nombre
      })) : [];
      console.log('[NewMateriaModal] Academias mapeadas:', mapped);
      setAcademias(mapped);
    }).catch(err => {
      console.error('[NewMateriaModal] Error al cargar academias:', err);
      setAcademias([]);
    });
  }, [open]);

  // Sincronizar academia cuando el catálogo de academias se carga después del initialData
  useEffect(() => {
    if (open && initialData && academias.length > 0 && !form.academia) {
      const academiaId = String(initialData.academia || '');
      if (academiaId && academias.some(a => a.id === academiaId)) {
        setForm(f => ({ ...f, academia: academiaId }));
        console.log('[NewMateriaModal] Sincronizando academia:', academiaId);
      }
    }
  }, [open, initialData, academias, form.academia]);

  useEffect(() => {
    if (open && !readOnly) {
      const first = dialogRef.current?.querySelector('input, select, textarea');
      first?.focus();
    }
  }, [open, readOnly]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Cargar programas para el selector del tab de créditos
  useEffect(() => {
    if (!open) return;
    setLoadingProgramas(true);
    getPrograms()
      .then(data => setProgramas(Array.isArray(data) ? data : []))
      .catch(() => setProgramas([]))
      .finally(() => setLoadingProgramas(false));
  }, [open]);

  // Cargar planes de estudio de la materia al entrar al tab de créditos
  useEffect(() => {
    if (activeTab !== 'creditos' || !canUseCreditsTab) return;
    setLoadingPlanes(true);
    getPlanesEstudioPorMateria(idMateria)
      .then(data => setPlanes(data))
      .catch(() => setPlanes([]))
      .finally(() => setLoadingPlanes(false));
  }, [activeTab, canUseCreditsTab, idMateria]);

  // Resetear tab al cerrar
  useEffect(() => {
    if (!open) {
      setActiveTab('general');
      setCreditoMsg(null);
      setCreditoForm({ id_programa: '', creditos_asignados: '' });
    }
  }, [open]);

  if (!open) return null;

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return;
    onSubmit?.(form);
  };

  const getTitle = () => {
    if (mode === 'view') return 'Ver Materia';
    if (mode === 'edit') return 'Editar Materia';
    return 'Nueva Materia';
  };

  const handleGuardarCredito = async () => {
    if (!creditoForm.id_programa) {
      setCreditoMsg({ type: 'error', text: 'Selecciona un programa.' });
      return;
    }
    if (creditoForm.creditos_asignados === '' || creditoForm.creditos_asignados === null) {
      setCreditoMsg({ type: 'error', text: 'Ingresa los créditos.' });
      return;
    }
    const credVal = parseInt(creditoForm.creditos_asignados, 10);
    if (!Number.isFinite(credVal) || credVal < 0) {
      setCreditoMsg({ type: 'error', text: 'Los créditos deben ser un número entero >= 0.' });
      return;
    }
    setSavingCredito(true);
    setCreditoMsg(null);
    try {
      await upsertCreditosPrograma({
        id_materia: idMateria,
        id_programa: Number(creditoForm.id_programa),
        creditos_asignados: credVal,
      });
      setCreditoMsg({ type: 'ok', text: 'Créditos guardados correctamente.' });
      setCreditoForm({ id_programa: '', creditos_asignados: '' });
      // Recargar tabla de planes
      const nuevos = await getPlanesEstudioPorMateria(idMateria);
      setPlanes(nuevos);
    } catch (err) {
      setCreditoMsg({ type: 'error', text: `Error al guardar: ${err.message}` });
    } finally {
      setSavingCredito(false);
    }
  };

  return (
    <div className={styles.modalBackdrop} role="presentation" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose?.();
    }}>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-labelledby="materia-title" ref={dialogRef}>
        <div className={styles.modalHeader}>
          <h2 id="materia-title" className={styles.modalTitle}>{getTitle()}</h2>
          <button className={styles.modalClose} aria-label="Cerrar" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', padding: '0 1.5rem' }}>
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            style={{
              padding: '0.6rem 1.2rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'general' ? '700' : '400',
              color: activeTab === 'general' ? '#1d4ed8' : '#6b7280',
              borderBottom: activeTab === 'general' ? '3px solid #1d4ed8' : '3px solid transparent',
              marginBottom: '-2px',
              fontSize: '0.9rem',
            }}
          >
            Información General
          </button>
          <button
            type="button"
            onClick={() => canUseCreditsTab && setActiveTab('creditos')}
            style={{
              padding: '0.6rem 1.2rem',
              border: 'none',
              background: 'none',
              cursor: canUseCreditsTab ? 'pointer' : 'not-allowed',
              fontWeight: activeTab === 'creditos' ? '700' : '400',
              color: activeTab === 'creditos' ? '#1d4ed8' : (canUseCreditsTab ? '#6b7280' : '#d1d5db'),
              borderBottom: activeTab === 'creditos' ? '3px solid #1d4ed8' : '3px solid transparent',
              marginBottom: '-2px',
              fontSize: '0.9rem',
              opacity: canUseCreditsTab ? 1 : 0.55,
            }}
            title={!canUseCreditsTab ? 'Guarda la materia primero para configurar créditos por programa' : ''}
          >
            Créditos por Programa
          </button>
        </div>

        {/* Tab: Información General */}
        {activeTab === 'general' && (
          <form className={styles.modalBody} onSubmit={handleSubmit}>
            <div className={styles.modalGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Nombre de la Materia</label>
                <input className={styles.input} name="nombre" value={form.nombre} onChange={handleChange} disabled={readOnly} required />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Clave</label>
                <input className={styles.input} name="clave" value={form.clave} onChange={handleChange} disabled={readOnly} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tipo de Materia</label>
                <select className={styles.select} name="tipo" value={form.tipo} onChange={handleChange} disabled={readOnly} required style={{ color: '#222' }}>
                  <option value="">— Selecciona —</option>
                  <option value="Institucional">Institucional</option>
                  <option value="Compartida">Compartida</option>
                  <option value="Específica">Específica</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Institución</label>
                <select className={styles.select} name="institucion" value={form.institucion} onChange={handleChange} disabled={readOnly} required style={{ color: '#222' }}>
                  <option value="">— Selecciona una institución —</option>
                  {Array.isArray(instituciones) && instituciones.length > 0 ? (
                    instituciones.map(inst => (
                      <option key={inst.id} value={String(inst.id)}>{inst.name}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando instituciones...</option>
                  )}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Sección</label>
                <select className={styles.select} name="seccion" value={form.seccion} onChange={handleChange} disabled={readOnly} required style={{ color: '#222' }}>
                  <option value="">— Selecciona una sección —</option>
                  {Array.isArray(secciones) && secciones.length > 0 ? (
                    secciones.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nombre}</option>
                    ))
                  ) : (
                    <option value="" disabled>No hay secciones disponibles</option>
                  )}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Academia</label>
                <select className={styles.select} name="academia" value={form.academia} onChange={handleChange} disabled={readOnly} style={{ color: '#222' }}>
                  <option value="">— Selecciona una academia —</option>
                  {Array.isArray(academias) && academias.length > 0 ? (
                    academias.map(a => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))
                  ) : (
                    <option value="" disabled>No hay academias disponibles</option>
                  )}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Horas Frente Asesor</label>
                <input className={styles.input} name="horasDocentesSemana" value={form.horasDocentesSemana} onChange={handleChange} disabled={readOnly} inputMode="numeric" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Horas Totales por Semana</label>
                <input className={styles.input} name="horasTotalesSemana" value={form.horasTotalesSemana} onChange={handleChange} disabled={readOnly} inputMode="numeric" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Descripción</label>
                <textarea className={styles.textarea} rows={2} name="descripcion" value={form.descripcion} onChange={handleChange} disabled={readOnly} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Objetivos</label>
                <textarea className={styles.textarea} rows={2} name="objetivos" value={form.objetivos} onChange={handleChange} disabled={readOnly} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Estado</label>
                <div className={styles.statusSelectWrapper}>
                  <span className={`${styles.statusDot} ${form.estado === 'ACTIVO' ? styles.dotGreen : styles.dotRed}`} />
                  <select
                    className={`${styles.statusSelect} ${form.estado === 'ACTIVO' ? styles.activeSelect : styles.inactiveSelect}`}
                    name="estado"
                    value={form.estado}
                    onChange={handleChange}
                    disabled={readOnly}
                  >
                    <option value="ACTIVO">ACTIVO</option>
                    <option value="INACTIVO">INACTIVO</option>
                  </select>
                </div>
              </div>
            </div>
            <div className={styles.modalActions}>
              {!readOnly && (
                <button type="submit" className={styles.primaryButton}>
                  {mode === 'edit' ? 'ACTUALIZAR' : 'GUARDAR'}
                </button>
              )}
              <button type="button" className={styles.secondaryButton} onClick={onClose}>
                {readOnly ? 'CERRAR' : 'CANCELAR'}
              </button>
            </div>
          </form>
        )}

        {/* Tab: Créditos por Programa */}
        {activeTab === 'creditos' && (
          <div className={styles.modalBody}>
            {!canUseCreditsTab ? (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>
                Guarda la materia primero para poder configurar créditos por programa.
              </p>
            ) : (
              <>
                {/* Formulario para agregar/actualizar crédito */}
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
                  <p style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#374151', fontSize: '0.9rem' }}>
                    Asignar créditos a un programa
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '0.75rem', alignItems: 'flex-end' }}>
                    <div>
                      <label className={styles.label}>Programa</label>
                      <select
                        className={styles.select}
                        value={creditoForm.id_programa}
                        onChange={e => setCreditoForm(f => ({ ...f, id_programa: e.target.value }))}
                        style={{ color: '#222' }}
                        disabled={loadingProgramas}
                      >
                        <option value="">— Selecciona programa —</option>
                        {programas
                          .map(p => (
                            <option key={p.id} value={String(p.id)}>{p.name || p.nombre_programa}</option>
                          ))
                        }
                      </select>
                    </div>
                    <div>
                      <label className={styles.label}>Créditos asignados</label>
                      <input
                        className={styles.input}
                        type="number"
                        min="0"
                        value={creditoForm.creditos_asignados}
                        onChange={e => setCreditoForm(f => ({ ...f, creditos_asignados: e.target.value }))}
                        placeholder="ej. 6"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={handleGuardarCredito}
                        disabled={savingCredito}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {savingCredito ? 'Guardando...' : 'Guardar Créditos'}
                      </button>
                    </div>
                  </div>
                  {creditoMsg && (
                    <p style={{
                      marginTop: '0.6rem',
                      fontSize: '0.85rem',
                      color: creditoMsg.type === 'ok' ? '#166534' : '#991b1b',
                      background: creditoMsg.type === 'ok' ? '#dcfce7' : '#fee2e2',
                      padding: '0.35rem 0.75rem',
                      borderRadius: '5px',
                    }}>
                      {creditoMsg.text}
                    </p>
                  )}
                </div>

                {/* Tabla de asignaciones existentes */}
                <p style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem' }}>
                  Programas que contienen esta materia
                </p>
                {loadingPlanes ? (
                  <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Cargando...</p>
                ) : planes.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: '0.9rem', fontStyle: 'italic' }}>
                    Esta materia aún no está asignada a ningún programa.
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                          <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#374151' }}>Programa</th>
                          <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#374151' }}>Créditos asignados</th>
                          <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#374151' }}>Obligatoria</th>
                        </tr>
                      </thead>
                      <tbody>
                        {planes.map(plan => (
                          <tr key={plan.id_plan_estudio} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#111827' }}>
                              {plan.nombre_programa || `Programa #${plan.id_programa}`}
                            </td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: '600', color: '#1d4ed8' }}>
                              {plan.creditos_asignados ?? '—'}
                            </td>
                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#374151' }}>
                              {plan.es_obligatoria ? 'Sí' : 'No'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className={styles.modalActions} style={{ marginTop: '1rem' }}>
                  <button type="button" className={styles.secondaryButton} onClick={onClose}>
                    CERRAR
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

